import type {
  CalculationSettingsDto,
  CalculationSettingsRequestBody,
} from '@/api';
import { CalculationType, PriceUnitType } from '@/api';
import {
  formatNumberInput,
  isValidNumeric,
  parseNumberInput,
  parseOptionalNumberInput,
} from '@/utils/number';
import type { PriceUnitCalculationSettings } from './types';

export type PriceUnitSettingsValidation = {
  isMissingCalculationType: boolean;
  isMissingRm: boolean;
  isMissingRrp: boolean;
  isMissingBreakEven: boolean;
  isMissingTargetRrp: boolean;
  isInvalidTargetLpIndex: boolean;
  isInvalidLpFmcTarget: boolean;
  isInvalidThreshold: boolean;
  isInvalidAboveThreshold: boolean;
  isInvalidBelowThreshold: boolean;
  hasValidationErrors: boolean;
};

export const comparePriceUnitIds = (left: string, right: string): number =>
  Number(left) - Number(right);

/**
 * Returns true when the given calculation type disables RM and RRP sub-methods.
 */
export const isRmRrpDisabled = (calculationType?: CalculationType): boolean =>
  calculationType === CalculationType.LpOnly ||
  calculationType === CalculationType.LicensedProducts;

export const isCalculationTypeLicensedProducts = (
  calculationType?: CalculationType,
): boolean => calculationType === CalculationType.LicensedProducts;

/**
 * Target driven calculates RM% from LP and RRP, so it has no RM sub-method.
 */
export const isCalculationTypeTargetDrivenLpAndRrp = (
  calculationType?: CalculationType,
): boolean =>
  calculationType === CalculationType.TargetDrivenLpAndRrpCalculatedRm;

export const mapCalculationSettingsToPriceUnitSettings = (
  settings: CalculationSettingsDto,
): PriceUnitCalculationSettings => ({
  id: settings.id,
  priceUnitId: String(settings.priceUnit.id),
  priceUnitName: settings.priceUnit.name || String(settings.priceUnit.id),
  priceUnitType: settings.priceUnit.type,
  year: String(settings.year),
  calculationType: settings.calculationType,
  calculationBasisValue: settings.calculationBasisPriceUnit?.id
    ? String(settings.calculationBasisPriceUnit.id)
    : undefined,
  autoCalcEnabled: settings.enableAutoCalc,
  breakEvenFactor: formatNumberInput(settings.breakEvenFactor),
  targetRrpIndex: formatNumberInput(settings.targetRrpIndex),
  lpFmcTarget: formatNumberInput(settings.fmcTarget),
  targetLpIndex: formatNumberInput(settings.targetLpIndex),
  aboveThresholdMax: formatNumberInput(settings.aboveThresholdMax),
  aboveThresholdMin: formatNumberInput(settings.aboveThresholdMin),
  belowThresholdMax: formatNumberInput(settings.belowThresholdMax),
  belowThresholdMin: formatNumberInput(settings.belowThresholdMin),
  threshold: formatNumberInput(settings.priceThreshold),
  rmPercentage: settings.rmCalculationType,
  rrpType: settings.rrpCalculationType,
});

export const mapPriceUnitSettingsToRequest = (
  priceUnitSettings: PriceUnitCalculationSettings,
): CalculationSettingsRequestBody => {
  const clearRmRrp = isRmRrpDisabled(priceUnitSettings.calculationType);
  const clearRm =
    clearRmRrp ||
    isCalculationTypeTargetDrivenLpAndRrp(priceUnitSettings.calculationType);

  return {
    year: Number(priceUnitSettings.year),
    priceUnitId: Number(priceUnitSettings.priceUnitId),
    calculationType: priceUnitSettings.calculationType,
    rmCalculationType: clearRm ? undefined : priceUnitSettings.rmPercentage,
    rrpCalculationType: clearRmRrp ? undefined : priceUnitSettings.rrpType,
    fmcTarget: parseNumberInput(priceUnitSettings.lpFmcTarget),
    targetLpIndex: parseNumberInput(priceUnitSettings.targetLpIndex),
    targetRrpIndex: parseOptionalNumberInput(priceUnitSettings.targetRrpIndex),
    breakEvenFactor: parseOptionalNumberInput(
      priceUnitSettings.breakEvenFactor,
    ),
    priceThreshold: parseNumberInput(priceUnitSettings.threshold),
    aboveThresholdMin: parseNumberInput(priceUnitSettings.aboveThresholdMin),
    aboveThresholdMax: parseNumberInput(priceUnitSettings.aboveThresholdMax),
    belowThresholdMin: parseNumberInput(priceUnitSettings.belowThresholdMin),
    belowThresholdMax: parseNumberInput(priceUnitSettings.belowThresholdMax),
    calculationBasisPriceUnitId: parseOptionalNumberInput(
      priceUnitSettings.calculationBasisValue,
    ),
    enableAutoCalc: priceUnitSettings.autoCalcEnabled,
  };
};

export const getUniqueValues = (
  priceUnitSettings: PriceUnitCalculationSettings[],
  selector: (
    settings: PriceUnitCalculationSettings,
  ) => string | string[] | undefined,
): string[] => {
  const allValues = priceUnitSettings.flatMap((settings) => {
    const value = selector(settings);
    if (Array.isArray(value)) return value;
    if (value) return [value];
    return [];
  });
  return [...new Set(allValues)].sort();
};

const isInvalidThresholdRange = (
  minValue: string | undefined,
  maxValue: string | undefined,
  isSimulationSettingsInherited: boolean,
): boolean => {
  const min = minValue ?? '';
  const max = maxValue ?? '';

  if (isSimulationSettingsInherited) {
    return false;
  }

  if (!isValidNumeric(min) || !isValidNumeric(max)) {
    return true;
  }

  return parseNumberInput(min) > parseNumberInput(max);
};

export const getPriceUnitSettingsValidation = (
  priceUnitSettings: PriceUnitCalculationSettings,
  simulationSettings: PriceUnitCalculationSettings,
  isSimulationSettingsInherited: boolean,
): PriceUnitSettingsValidation => {
  const isLead = priceUnitSettings.priceUnitType === PriceUnitType.Lead;

  const isCalculationTypeLicensedProducts =
    priceUnitSettings.calculationType === CalculationType.LicensedProducts;
  const isCalcTypeTargetDrivenLpAndRrp = isCalculationTypeTargetDrivenLpAndRrp(
    priceUnitSettings.calculationType,
  );
  const isMissingCalculationType =
    !isLead && !priceUnitSettings.calculationType;
  const rmRrpDisabled = isRmRrpDisabled(priceUnitSettings.calculationType);

  const isMissingRm =
    !rmRrpDisabled &&
    !isCalcTypeTargetDrivenLpAndRrp &&
    !priceUnitSettings.rmPercentage;
  const isMissingRrp = !rmRrpDisabled && !priceUnitSettings.rrpType;
  const isMissingBreakEven =
    isCalculationTypeLicensedProducts &&
    !isValidNumeric(priceUnitSettings.breakEvenFactor);
  const isMissingTargetRrp =
    (isCalculationTypeLicensedProducts || isCalcTypeTargetDrivenLpAndRrp) &&
    !isValidNumeric(priceUnitSettings.targetRrpIndex);

  const isInvalidTargetLpIndex =
    !isSimulationSettingsInherited &&
    !isValidNumeric(simulationSettings.targetLpIndex);
  const isInvalidLpFmcTarget =
    !isSimulationSettingsInherited &&
    !isValidNumeric(simulationSettings.lpFmcTarget);
  const isInvalidThreshold =
    !isSimulationSettingsInherited &&
    !isValidNumeric(simulationSettings.threshold);
  const isInvalidAboveThreshold = isInvalidThresholdRange(
    simulationSettings.aboveThresholdMin,
    simulationSettings.aboveThresholdMax,
    isSimulationSettingsInherited,
  );
  const isInvalidBelowThreshold = isInvalidThresholdRange(
    simulationSettings.belowThresholdMin,
    simulationSettings.belowThresholdMax,
    isSimulationSettingsInherited,
  );

  const hasCalculationSettingsValidationErrors =
    isMissingCalculationType ||
    isMissingRm ||
    isMissingRrp ||
    isMissingBreakEven ||
    isMissingTargetRrp;

  const hasSimulationSettingsValidationErrors =
    isInvalidTargetLpIndex ||
    isInvalidLpFmcTarget ||
    isInvalidThreshold ||
    isInvalidAboveThreshold ||
    isInvalidBelowThreshold;

  const hasValidationErrors = isLead
    ? hasSimulationSettingsValidationErrors
    : hasCalculationSettingsValidationErrors ||
      hasSimulationSettingsValidationErrors;

  return {
    isMissingCalculationType,
    isMissingRm,
    isMissingRrp,
    isMissingBreakEven,
    isMissingTargetRrp,
    isInvalidTargetLpIndex,
    isInvalidLpFmcTarget,
    isInvalidThreshold,
    isInvalidAboveThreshold,
    isInvalidBelowThreshold,
    hasValidationErrors,
  };
};
